import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Copy, Check, ChevronLeft, ChevronRight, PlayCircle, FileText, Lightbulb, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SEOHead from "../components/SEOHead";

import { useTranslation } from 'react-i18next';
function CopyablePrompt({ content, title }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success("Prompt copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div data-testid="prompt-block" className="border border-amber-500/40 bg-amber-500/5 my-6">
      <SEOHead
        title={t('course_lesson.course_lesson_max_employee')}
        description={t('course_lesson.max_employee_course_lesson_learn_how_to_deploy')}
        url="/dashboard/course"
        noIndex
      />
      <div className="flex items-center justify-between border-b border-amber-500/30 px-4 py-3 bg-black/40">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
            {title || "PROMPT"}
          </span>
        </div>
        <button
          onClick={onCopy}
          data-testid="copy-prompt-button"
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "COPIED" : "COPY"}
        </button>
      </div>
      <pre className="p-5 whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-amber-100/90 overflow-x-auto">
        {content}
      </pre>
    </div>
  );
}

function CalloutBlock({ content, title }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-5 my-6 flex gap-4">
      <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
      <div>
        {title && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-1">{title}</div>}
        <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{content}</div>
      </div>
    </div>
  );
}

function VideoBlock({ content, title }) {
  // Accept YouTube URLs and embed them
  const embed = useMemo(() => {
    const yt = content.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([A-Za-z0-9_-]{6,})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vimeo = content.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    return content;
  }, [content]);
  return (
    <div className="my-6">
      {title && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-2">{title}</div>}
      <div className="aspect-video border border-zinc-900 bg-black">
        <iframe
          src={embed} title={title || "video"} className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

function PdfBlock({ content, title }) {
  // content is an URL to the PDF (uploaded asset or admin-provided URL)
  return (
    <div className="my-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-500" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
            {title || "PDF DOCUMENT"}
          </span>
        </div>
        <a href={content} target="_blank" rel="noreferrer" className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:text-amber-500">
          OPEN IN NEW TAB ↗
        </a>
      </div>
      <div className="border border-zinc-900 h-[800px] bg-zinc-950">
        <iframe src={content} title={title || "pdf"} className="w-full h-full" />
      </div>
    </div>
  );
}

function renderMarkdown(text) {
  // Minimal markdown: ## headings, bold **x**, code `x`, lists -, paragraphs
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-2 my-4 text-zinc-300">
        {listBuf.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: li }} />)}
      </ul>);
      listBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/`([^`]+?)`/g, "<code class='bg-zinc-900 text-amber-400 px-1.5 py-0.5 font-mono text-[13px]'>$1</code>");
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^##\s/.test(line)) { flushList(); out.push(<h3 key={i} className="font-display text-2xl uppercase mt-10 mb-4">{line.slice(3)}</h3>); return; }
    if (/^#\s/.test(line)) { flushList(); out.push(<h2 key={i} className="font-display text-3xl uppercase mt-12 mb-4">{line.slice(2)}</h2>); return; }
    if (/^[-*]\s/.test(line)) { listBuf.push(inline(line.replace(/^[-*]\s/, ""))); return; }
    if (line.trim() === "") { flushList(); return; }
    flushList();
    out.push(<p key={i} className="text-zinc-300 leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
  });
  flushList();
  return out;
}

export default function CourseLesson() {
  const { t } = useTranslation();
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { onComplete, modules } = useOutletContext();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/course/lessons/${lessonId}`)
      .then(({ data }) => setLesson(data))
      .finally(() => setLoading(false));
  }, [lessonId]);

  // Compute prev/next across modules
  const { prev, next } = useMemo(() => {
    const flat = [];
    for (const m of modules || []) {
      for (const l of m.lessons || []) flat.push(l);
    }
    const idx = flat.findIndex((l) => l.lesson_id === lessonId);
    return { prev: idx > 0 ? flat[idx - 1] : null, next: idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null };
  }, [modules, lessonId]);

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/course/progress/${lessonId}`);
      onComplete && onComplete();
      toast.success("Lesson completed");
      setLesson((l) => l ? { ...l, completed: true } : l);
    } catch {
      toast.error("Could not save progress");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) return <div className="p-12 font-mono text-sm text-zinc-500">{t('course.loading_lesson')}</div>;
  if (!lesson) return <div className="p-12 font-mono text-sm text-zinc-500">{t('course.lesson_not_found')}</div>;

  return (
    <article data-testid="course-lesson" className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
        // LESSON {lesson.order + 1} · {lesson.module_id.toUpperCase()}
      </div>
      <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mb-6">{lesson.title}</h1>
      {lesson.summary && (
        <p className="text-zinc-400 text-lg leading-relaxed border-l-2 border-amber-500 pl-5 mb-10">
          {lesson.summary}
        </p>
      )}

      <div className="prose prose-invert max-w-none">
        {(lesson.blocks || []).map((b, i) => {
          if (b.type === "prompt") return <CopyablePrompt key={i} content={b.content} title={b.title} />;
          if (b.type === "video") return <VideoBlock key={i} content={b.content} title={b.title} />;
          if (b.type === "pdf") return <PdfBlock key={i} content={b.content} title={b.title} />;
          if (b.type === "callout") return <CalloutBlock key={i} content={b.content} title={b.title} />;
          return (
            <div key={i} className="my-6">
              {b.title && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-2">{b.title}</div>}
              <div>{renderMarkdown(b.content)}</div>
            </div>
          );
        })}
      </div>

      {/* Footer nav */}
      <div className="mt-16 border-t border-zinc-900 pt-8 flex items-center justify-between gap-4 flex-wrap">
        <button
          disabled={!prev}
          onClick={() => prev && navigate(`/dashboard/course/${prev.lesson_id}`)}
          data-testid="prev-lesson-button"
          className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed text-zinc-300 px-5 py-3 font-mono text-[11px] uppercase tracking-wider transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> {prev ? prev.title : "No previous"}
        </button>

        <button
          onClick={markComplete}
          disabled={completing || lesson.completed}
          data-testid="complete-lesson-button"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs transition-colors"
        >
          {lesson.completed ? <><Check className="w-4 h-4" /> Completed</> :
           completing ? "Saving..." : <>{t('common.mark_complete')}</>}
        </button>

        <button
          disabled={!next}
          onClick={() => next && navigate(`/dashboard/course/${next.lesson_id}`)}
          data-testid="next-lesson-button"
          className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed text-zinc-300 px-5 py-3 font-mono text-[11px] uppercase tracking-wider transition-colors"
        >
          {next ? next.title : "No next"} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </article>
  );
}
